import { Connector } from 'Webapp/utils/connector';
import {
  isArticleRoute,
  isSectionRoute,
} from 'Webapp/shared/app/redux/selectors/routing';
import { getPrimarySection } from 'Webapp/shared/app/redux/selectors/section';
import { getCurrentArticle } from 'Webapp/shared/app/redux/selectors/article';
import { userStateDataSelector } from 'Webapp/shared/app/redux/selectors/profile';
import FlapUtil from 'Utils/content/flap-util.js';
import SectionUtil from 'Utils/content/section-util';

export const AD_PAGE_TYPES = {
  TOPIC: 'topic',
  MAGAZINE: 'magazine',
  PROFILE: 'profile',
  ITEM: 'item',
} as const;

export type AdPageType = typeof AD_PAGE_TYPES[keyof typeof AD_PAGE_TYPES];

/**
 * Page type used for GPT page-level targeting and to decide which pages get
 * the leaderboard placement: topic / magazine / profile section pages and
 * on-site item (article) pages. Anything else (home, search, storyboards,
 * auth, ...) returns null and gets no ads.
 */
const adPageType = (state: Flipboard.State): AdPageType | null => {
  if (isArticleRoute(state)) {
    return AD_PAGE_TYPES.ITEM;
  }
  if (!isSectionRoute(state)) {
    return null;
  }
  // Route-first: the matched route is authoritative for what kind of page
  // this is. getPrimarySection is NOT — on topic pages the sections array
  // can lead with auxiliary sections (e.g. the explore-spotlight sidebar),
  // which mislabeled /topic/* pages as page_type=profile (observed live in
  // the GAM publisher console, 2026-07-30).
  const { path, params } = state.app.routing;
  if (path === '/:shortcut/:shortcutId' && params?.shortcut === 'topic') {
    return AD_PAGE_TYPES.TOPIC;
  }
  if (path === '/@:username') {
    return AD_PAGE_TYPES.PROFILE;
  }
  const section = getPrimarySection(state);
  if (!section || section.placeholder || section.isStoryboard) {
    return null;
  }
  if (section.isTopic || section.isSmartMagazine) {
    return AD_PAGE_TYPES.TOPIC;
  }
  if (section.isMagazine || path === '/@:username/:slug') {
    return AD_PAGE_TYPES.MAGAZINE;
  }
  if (section.isProfile) {
    return AD_PAGE_TYPES.PROFILE;
  }
  return null;
};

/**
 * Topic slug key-value where trivially available (topic sections carry a
 * topicTag at runtime).
 */
const adTopicSlug = (state: Flipboard.State): string | null => {
  if (!isSectionRoute(state)) {
    return null;
  }
  const section = getPrimarySection(state) as
    | (Flipboard.Section & { topicTag?: string })
    | undefined;
  return section?.topicTag || null;
};

/** How many top-of-feed items count as "adjacent" to a page-level slot. */
const PAGE_SLOT_ADJACENT_ITEMS = 2;

const EMPTY_ITEMS: Array<Flipboard.Item> = [];
let lastAdjacentKey: Array<unknown> = [];
let lastAdjacentItems: Array<Flipboard.Item> = EMPTY_ITEMS;

const sameKey = (a: Array<unknown>, b: Array<unknown>) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * The content items adjacent to the page-level slots (leaderboard, article
 * rail), for brand-safety key-values: the article itself on an item page;
 * on a section page the first renderable items of the feed actually on
 * screen, which is the topic subtab the page reports through
 * app.adsAdjacentFeedRemoteId when one is selected and the primary section
 * otherwise. Items go through SectionUtil.itemsToRender so muted or
 * non-renderable items the reader never sees are not described to the ad.
 * Memoized on the inputs' store references so connected components don't
 * re-render on unrelated store changes.
 */
const adBrandSafetyItems = (state: Flipboard.State): Array<Flipboard.Item> => {
  let key: Array<unknown> = [];
  let compute: () => Array<Flipboard.Item> = () => EMPTY_ITEMS;
  if (isArticleRoute(state)) {
    const article = getCurrentArticle(state) as Flipboard.Item | undefined;
    key = ['article', article || null];
    compute = () => (article ? [article] : EMPTY_ITEMS);
  } else if (isSectionRoute(state)) {
    const { entries } = state.sections;
    const displayedRemoteId = state.app.adsAdjacentFeedRemoteId;
    const section = ((displayedRemoteId &&
      FlapUtil.getSectionByRemoteId(displayedRemoteId, entries)) ||
      getPrimarySection(state)) as Flipboard.Section | undefined;
    const sectionItems = (section?.items ||
      null) as Array<Flipboard.Item> | null;
    const currentUserData = userStateDataSelector(state);
    key = ['section', section || null, sectionItems, currentUserData];
    compute = () => {
      if (!section || !sectionItems?.length) {
        return EMPTY_ITEMS;
      }
      const renderable = SectionUtil.itemsToRender(
        section,
        sectionItems,
        currentUserData,
      ) as Array<Flipboard.Item>;
      return renderable.slice(0, PAGE_SLOT_ADJACENT_ITEMS);
    };
  }
  if (!sameKey(key, lastAdjacentKey)) {
    lastAdjacentKey = key;
    lastAdjacentItems = compute();
  }
  return lastAdjacentItems;
};

const selectors = {
  adPageType,
  adTopicSlug,
  adBrandSafetyItems,
};

export type ConnectAdTargetingProps = Flipboard.ConnectorProps<
  typeof selectors
>;

const connectAdTargeting: Connector = {
  selectors,
};

export default connectAdTargeting;
